import React from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import ChartTrendUpIcon from "@atlaskit/icon/core/chart-trend-up";
import LightbulbIcon from "@atlaskit/icon/core/lightbulb";
import SearchIcon from "@atlaskit/icon/core/search";
import { token } from "@atlaskit/tokens";

import { wacToken } from "@/libraries/design/foundations";

type ColdStartButtonProps = {
    /**
     * Icon element to display before the button text
     */
    icon: React.ReactNode;
    /**
     * Button text/label to display
     */
    buttonText: string;
    /**
     * Optional onClick handler
     */
    onClick?: () => void;
};

const buttonStyles = css({
    "display": "flex",
    "flexDirection": "row",
    "justifyContent": "center",
    "alignItems": "center",
    "gap": "6px",
    "padding": `0px ${token("space.100")}`,
    "borderRadius": "4px",
    "border": `1px solid ${token("color.border")}`,
    "background": token("color.background.neutral"),
    "cursor": "pointer",
    "transition": "all 0.2s ease-in-out",
    "minHeight": "32px",
    "&:hover": {
        background: token("color.background.neutral.hovered"),
        borderColor: token("color.border.bold")
    },
    "&:active": {
        background: token("color.background.neutral.pressed"),
        borderColor: token("color.border.bold")
    }
});

const iconStyles = css({
    width: "16px",
    height: "16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
});

const textStyles = css({
    fontFamily: "Charlie Text, sans-serif",
    fontWeight: 400,
    fontSize: "14px",
    lineHeight: "1.4285714285714286em",
    textAlign: "center",
    color: wacToken("color.neutral.light800"),
    margin: 0,
    padding: 0,
    whiteSpace: "nowrap"
});

const buttonsContainerStyles = css({
    display: "flex",
    flexDirection: "row",
    gap: "8px",
    padding: "16px 12px",
    width: "100%",
    justifyContent: "center",
    flexWrap: "wrap"
});

type ColdStartButtonOption = {
    icon: React.ReactNode;
    labelMessageId: string;
    labelDefaultMessage: string;
    promptMessageId: string;
    promptDefaultMessage: string;
};

const COLD_START_BUTTONS: ColdStartButtonOption[] = [
    {
        icon: <ChartTrendUpIcon label="Chart trend up" />,
        labelMessageId: "RovoChat.coldStart.stayUpToDate",
        labelDefaultMessage: "Stay up to date",
        promptMessageId: "RovoChat.coldStart.stayUpToDatePrompt",
        promptDefaultMessage: "What's new this week that I should know about?"
    },
    {
        icon: <SearchIcon label="Search" />,
        labelMessageId: "RovoChat.coldStart.findInformation",
        labelDefaultMessage: "Find information",
        promptMessageId: "RovoChat.coldStart.findInformationPrompt",
        promptDefaultMessage:
            "Help me find information about [topic or process]"
    },
    {
        icon: <LightbulbIcon label="Lightbulb" />,
        labelMessageId: "RovoChat.coldStart.brainstormIdeas",
        labelDefaultMessage: "Brainstorm ideas",
        promptMessageId: "RovoChat.coldStart.brainstormIdeasPrompt",
        promptDefaultMessage:
            "Let's brainstorm ideas for [project, campaign, or topic]."
    }
];

type ColdStartButtonsRowProps = {
    onButtonClick?: (prompt: string) => void;
};

export const ColdStartButton: React.FC<ColdStartButtonProps> = ({
    icon,
    buttonText,
    onClick
}) => {
    return (
        <button css={buttonStyles} onClick={onClick} type="button">
            <div css={iconStyles}>{icon}</div>
            <span css={textStyles}>{buttonText}</span>
        </button>
    );
};

/**
 * ColdStartButtonsRow displays a row of pre-filled action buttons for the Rovo Chat
 */
export const ColdStartButtonsRow: React.FC<ColdStartButtonsRowProps> = ({
    onButtonClick
}) => {
    const intl = useIntl();

    return (
        <div css={buttonsContainerStyles}>
            {COLD_START_BUTTONS.map((button) => {
                const label = intl.formatMessage({
                    id: button.labelMessageId,
                    defaultMessage: button.labelDefaultMessage
                });
                const prompt = intl.formatMessage({
                    id: button.promptMessageId,
                    defaultMessage: button.promptDefaultMessage
                });

                return (
                    <ColdStartButton
                        key={button.labelMessageId}
                        icon={button.icon}
                        buttonText={label}
                        onClick={() => onButtonClick?.(prompt)}
                    />
                );
            })}
        </div>
    );
};
